/**
 * ForecastCategoryPicker — RULE-FORECAST-1 hard gate (Ticket 1).
 *
 * Rep-facing dropdown that REFUSES selections above the timing-eligible
 * floor. On a blocked click, the bucket auto-downgrades to the highest
 * eligible value and an inline message cites the rule. The deal row shows
 * a red flag when the system flags it for manager attention.
 *
 * Brand: Navy #0B0B0F · Red #DC2626 · Off-white #FAFAF7
 */

import { useMemo, useState } from "react";
import { Lock } from "lucide-react";
import {
  evaluateBucketAttempt,
  effectiveBucket,
  needsManagerFlag,
  type ForecastBucket,
} from "../../../../shared/rule-forecast-1";
import DisciplineCoachingBlock from "@/components/discipline/DisciplineCoachingBlock";

const BLACK = "#0B0B0F";
const RED = "#DC2626";
const MUTED = "#8E8E94";
const AMBER = "#EAB308"; // W24: canon yellow per CANON_VISUALS_LOCKED §4 (was #CA8A04 — off-canon)

// Canon 5-bucket labels — CANON.md lines 167-186.
// Legacy PUSH / LOSS map to OMITTED. Both displayed as "Omit" for users.
// UI NOTE: score drives category; picker is preserved for legacy override
// workflows — flag for Bryan to confirm if this UI should be removed or
// made read-only once score-only assignment is fully wired.
const BUCKET_LABEL: Record<ForecastBucket, string> = {
  COMMIT: "Commit",
  BEST_CASE: "Best Case",
  PIPELINE: "Pipeline",
  // PUSH and LOSS are no longer selectable — both map to OMITTED.
  PUSH: "Omit",     // legacy: mapped to Omit
  LOSS: "Omit",     // legacy: mapped to Omit
  OMITTED: "Omit",
};

export interface ForecastCategoryPickerProps {
  compositeScore: number;
  timingPct: number | null;
  current: ForecastBucket | null;
  /** Called with the SYSTEM-ENFORCED bucket (post auto-downgrade). */
  onSave: (next: ForecastBucket, autoDowngraded: boolean) => void | Promise<void>;
  /** Lets a manager bypass the hard block; defaults to false (rep view). */
  managerOverride?: boolean;
  /** When provided, used as the row's data-testid prefix for tests. */
  testIdPrefix?: string;
  disabled?: boolean;
  /** Account ID for discipline coaching block context (Surface 3 — Sprint 1). */
  accountId?: number;
}

// Canon 5 selectable buckets. PUSH and LOSS removed; OMITTED replaces both.
// Upside not yet a separate ForecastBucket in shared/rule-forecast-1.ts —
// it is expressed as BEST_CASE in the shared type until that type is migrated.
// TODO: add UPSIDE to shared/rule-forecast-1.ts ForecastBucket when confirmed.
const SELECTABLE: ForecastBucket[] = ["COMMIT", "BEST_CASE", "PIPELINE", "OMITTED"];

export function ForecastCategoryPicker(props: ForecastCategoryPickerProps) {
  const {
    compositeScore,
    timingPct,
    current,
    onSave,
    managerOverride = false,
    testIdPrefix = "forecast-picker",
    disabled = false,
    accountId,
  } = props;

  const [attempted, setAttempted] = useState<ForecastBucket | null>(current);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const eff = useMemo(
    () => effectiveBucket(compositeScore, timingPct),
    [compositeScore, timingPct],
  );
  const flagged = useMemo(() => needsManagerFlag(timingPct), [timingPct]);

  async function handleClick(b: ForecastBucket) {
    if (disabled || saving) return;
    setAttempted(b);

    const decision = evaluateBucketAttempt({
      attempted: b,
      compositeScore,
      timingPct,
    });

    if (!decision.ok && !managerOverride) {
      setError(decision.reason);
      // Auto-downgrade: persist the system-enforced bucket immediately so
      // the rep cannot leave a higher value sitting in state on save.
      setSaving(true);
      try {
        await onSave(decision.effective, true);
        setAttempted(decision.effective);
      } finally {
        setSaving(false);
      }
      return;
    }

    setError(null);
    setSaving(true);
    try {
      await onSave(b, false);
      setAttempted(b);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      data-testid={`${testIdPrefix}-root`}
      style={{ fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: BLACK }}>Forecast category</span>
        {flagged && (
          <span
            data-testid={`${testIdPrefix}-manager-flag`}
            aria-label="Manager attention flag — timing confidence is 50%"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "2px 8px",
              borderRadius: 999,
              background: `${RED}1A`,
              color: RED,
              border: `1px solid ${RED}55`,
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.3,
            }}
          >
            <FlagIcon />
            Manager flag
          </span>
        )}
      </div>

      <div
        role="group"
        aria-label="Forecast category selector"
        data-testid={`${testIdPrefix}-buttons`}
        style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0,1fr))", gap: 6 }}
      >
        {SELECTABLE.map((b) => {
          const selected = attempted === b;
          const decision = evaluateBucketAttempt({
            attempted: b,
            compositeScore,
            timingPct,
          });
          const blockedForRep = !decision.ok && !managerOverride;
          return (
            <button
              key={b}
              type="button"
              disabled={disabled || saving}
              data-testid={`${testIdPrefix}-btn-${b}`}
              data-blocked={blockedForRep ? "true" : "false"}
              aria-pressed={selected}
              aria-disabled={blockedForRep}
              title={blockedForRep ? decision.reason : `Set to ${BUCKET_LABEL[b]}`}
              onClick={() => handleClick(b)}
              style={{
                padding: "8px 10px",
                fontSize: 12,
                fontWeight: 600,
                borderRadius: 6,
                border: selected ? `2px solid ${BLACK}` : `1px solid ${BLACK}33`,
                background: selected ? BLACK : blockedForRep ? "#F3F3F3" : "#fff",
                color: selected ? "#fff" : blockedForRep ? MUTED : BLACK,
                cursor: disabled || saving ? "not-allowed" : "pointer",
                opacity: blockedForRep ? 0.7 : 1,
                position: "relative",
              }}
            >
              {BUCKET_LABEL[b]}
              {blockedForRep && (
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    top: 2,
                    right: 4,
                    fontSize: 10,
                    color: RED,
                  }}
                  data-testid={`${testIdPrefix}-lock-${b}`}
                >
                  <Lock size={12} aria-label="Locked" />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {error && accountId ? (
        <DisciplineCoachingBlock
          ruleCode="RULE-FORECAST-1"
          entityKind="account"
          entityId={String(accountId)}
          scenarioKey={
            error.toLowerCase().includes("conv") || error.toLowerCase().includes("band")
              ? "commit_without_recent_conv"
              : "commit_without_close_date"
          }
          refusalMessage={error}
          refusalPayload={{ rule_code: "RULE-FORECAST-1", message: error }}
          attemptedAction={{ target_bucket: attempted, prev_bucket: current }}
        />
      ) : error ? (
        <div
          role="alert"
          data-testid={`${testIdPrefix}-error`}
          style={{
            marginTop: 8,
            padding: "8px 12px",
            background: `${RED}10`,
            border: `1px solid ${RED}55`,
            borderRadius: 6,
            color: BLACK,
            fontSize: 12,
            lineHeight: 1.5,
          }}
        >
          <strong style={{ color: RED }}>RULE-FORECAST-1: </strong>
          {error}
        </div>
      ) : null}

      <div style={{ marginTop: 6, fontSize: 11, color: MUTED }}>
        Highest eligible bucket:{" "}
        <strong
          data-testid={`${testIdPrefix}-effective`}
          style={{ color: BLACK }}
        >
          {BUCKET_LABEL[eff]}
        </strong>{" "}
        ·{" "}
        <span title="Composite score × declared timing %">
          composite {compositeScore} / timing{" "}
          {timingPct == null ? "—" : timingPct < 50 ? "<50%" : `${timingPct}%`}
        </span>
        {managerOverride && (
          <span
            style={{ marginLeft: 8, color: AMBER, fontWeight: 600 }}
            data-testid={`${testIdPrefix}-mgr-override`}
          >
            Manager override active
          </span>
        )}
      </div>
    </div>
  );
}

function FlagIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3 14V2.5C3 2.224 3.224 2 3.5 2H12l-2 3 2 3H3.5C3.224 8 3 7.776 3 7.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
